/**
 * The geometry and the pixels of a scan, kept apart from the camera so they can
 * be tested without one. Request of 4 October 2026 (second), section 5.
 */

/** The share of the shorter side of the picture the scan frame covers at no zoom. */
export const SCAN_REGION = 0.7;
/** The crop is scaled to at most this many pixels a side before decoding: plenty for a tray code. */
export const DECODE_MAX_SIDE = 720;

export interface Region {
  x: number;
  y: number;
  side: number;
}

/**
 * The centred square that is decoded, in the picture's own pixels. Digital zoom
 * narrows it, which is what makes a small or distant code fill the decoder.
 */
export function scanRegion(width: number, height: number, zoom = 1): Region {
  const side = Math.max(1, Math.round((Math.min(width, height) * SCAN_REGION) / Math.max(1, zoom)));
  return { x: Math.round((width - side) / 2), y: Math.round((height - side) / 2), side };
}

/**
 * Stretches the brightness of an RGBA picture so its darkest and lightest few
 * percent become black and white, in place, as grey.
 *
 * In ordinary lab lighting a phone's picture of a tray card is grey on grey:
 * the black modules are dark grey and the paper is mid grey. The decoder
 * thresholds that well when the range is wide and badly when it is narrow; this
 * widens it. Two percent at each end keeps a reflection or a shadow from
 * deciding the range.
 */
export function stretchContrast(data: Uint8ClampedArray): void {
  const pixels = data.length / 4;
  if (pixels === 0) return;
  const histogram = new Uint32Array(256);
  for (let i = 0; i < data.length; i += 4) {
    // Integer luma, close enough to Rec. 601 for thresholding.
    const grey = ((data[i] ?? 0) * 77 + (data[i + 1] ?? 0) * 150 + (data[i + 2] ?? 0) * 29) >> 8;
    data[i] = grey;
    histogram[grey] = (histogram[grey] ?? 0) + 1;
  }

  const cut = Math.floor(pixels * 0.02);
  let low = 0;
  for (let seen = 0; low < 255; low += 1) {
    seen += histogram[low] ?? 0;
    if (seen > cut) break;
  }
  let high = 255;
  for (let seen = 0; high > 0; high -= 1) {
    seen += histogram[high] ?? 0;
    if (seen > cut) break;
  }

  const span = high - low;
  for (let i = 0; i < data.length; i += 4) {
    const grey = data[i] ?? 0;
    const value = span <= 0 ? grey : Math.min(255, Math.max(0, Math.round(((grey - low) * 255) / span)));
    data[i] = value;
    data[i + 1] = value;
    data[i + 2] = value;
  }
}
